import axios from 'axios'

class CarService {
  constructor() {
    this.baseURL = null
    this.axiosInstance = null
    this.isConnected = false
    this.useExtension = false
  }

  // Helper to proxy requests via extension
  async proxyRequest(url, method = 'GET', body = null, headers = {}) {
    return new Promise((resolve, reject) => {
      const requestId = Date.now() + Math.random().toString()

      const handler = (event) => {
        if (event.source !== window || !event.data || event.data.type !== 'EZ_CONTROL_PROXY_RESPONSE') return
        if (event.data.requestId !== requestId) return

        window.removeEventListener('message', handler)

        const result = event.data.payload
        if (result.success) {
          // Emulate axios response structure
          resolve({
            data: result.data,
            status: result.status,
            statusText: 'OK'
          })
        } else {
          reject(new Error(result.error || 'Extension proxy failed'))
        }
      }

      window.addEventListener('message', handler)

      // Construct full URL
      const fullUrl = this.baseURL + url

      window.postMessage({
        type: 'PROXY_REQUEST',
        source: 'EZ_CONTROL_REACT',
        payload: { requestId, url: fullUrl, method, body, headers }
      }, '*')

      // Timeout
      setTimeout(() => {
        window.removeEventListener('message', handler)
        reject(new Error('Extension request timeout'))
      }, 5000)
    })
  }

  // Connect to a device
  async connect(deviceIP) {
    try {
      console.log('Attempting to connect to:', deviceIP)

      this.baseURL = `http://${deviceIP}`

      // Check if Extension is available
      if (window.__EZ_CONTROL_EXTENSION_ACTIVE__) {
        console.log('🧩 Using Chrome Extension for connection')
        this.useExtension = true
      } else {
        this.useExtension = false
        this.axiosInstance = axios.create({
          baseURL: this.baseURL,
          timeout: 5000,
          headers: {
            'Content-Type': 'application/json',
          }
        })
      }

      // Test connection by getting status
      console.log('Testing connection to:', this.baseURL + '/status')
      const status = await this.getStatus()

      this.isConnected = true
      console.log('Successfully connected to device:', this.baseURL)
      console.log('Device status:', status)
      return status
    } catch (error) {
      console.error('Connection failed:', error)
      this.isConnected = false
      throw new Error(`Failed to connect to ${deviceIP}: ${error.message}`)
    }
  }

  // Disconnect from device
  disconnect() {
    this.baseURL = null
    this.axiosInstance = null
    this.isConnected = false
    this.useExtension = false
  }

  // Get device status with CORS fallback
  async getStatus() {
    if (!this.baseURL) {
      throw new Error('Not connected to any device')
    }

    try {
      if (this.useExtension) {
        const response = await this.proxyRequest('/status')
        return response.data
      }

      const response = await this.axiosInstance.get('/status')
      return response.data
    } catch (error) {
      // If CORS fails, try without CORS (only for direct connection)
      if (!this.useExtension && (error.message.includes('CORS') || error.message.includes('Network Error'))) {
        console.log('CORS issue detected, trying alternative method...')
        try {
          // Use fetch with no-cors mode as fallback
          const response = await fetch(`${this.baseURL}/status`, {
            method: 'GET',
            mode: 'no-cors',
            timeout: 3000
          })

          if (response.type === 'opaque') {
            console.log('Device responding in no-cors mode')
            // Return a basic status object since we can't read the response
            return {
              name: 'EZ Control Device',
              version: '5.0.0',
              device: 'nodemcu',
              mode: 'wifi',
              status: 'connected',
              direction: 'STOP',
              speed: 0,
              isMoving: false,
              pinStates: {}
            }
          }
        } catch (noCorsError) {
          console.log('No-cors also failed:', noCorsError.message)
        }
      }
      throw new Error(`Failed to get status: ${error.message}`)
    }
  }

  // Test connection endpoint
  async testConnection() {
    if (!this.baseURL) {
      throw new Error('Not connected to any device')
    }

    try {
      if (this.useExtension) {
        const response = await this.proxyRequest('/test')
        return response.data
      }

      const response = await this.axiosInstance.get('/test')
      return response.data
    } catch (error) {
      throw new Error(`Failed to test connection: ${error.message}`)
    }
  }

  // Get device discovery info
  async getDiscoveryInfo() {
    if (!this.baseURL) {
      throw new Error('Not connected to any device')
    }

    try {
      if (this.useExtension) {
        const response = await this.proxyRequest('/discovery')
        return response.data
      }

      const response = await this.axiosInstance.get('/discovery')
      return response.data
    } catch (error) {
      throw new Error(`Failed to get discovery info: ${error.message}`)
    }
  }

  // Send car command with speed parameter
  async sendCarCommand(command, speed = null) {
    if (!this.baseURL) {
      throw new Error('Not connected to any device')
    }

    try {
      let url = `/${command}`

      if (speed !== null) {
        // For speed control, use query parameter
        if (command === 'car/speed') {
          url += `?speed=${speed}`
        } else {
          // For movement commands, use query parameter for speed
          url += `?speed=${speed}`
        }
      }

      if (this.useExtension) {
        const response = await this.proxyRequest(url, 'POST')
        return response.data
      }

      const response = await this.axiosInstance.post(url)
      return response.data
    } catch (error) {
      throw new Error(`Failed to send command ${command}: ${error.message}`)
    }
  }

  // GPIO Control Methods - Updated for NodeMCU.ino exactly
  async setPinState(pin, state) {
    if (!this.baseURL) {
      throw new Error('Not connected to any device')
    }

    try {
      console.log(`Setting pin ${pin} to ${state ? 'HIGH' : 'LOW'}`)

      if (this.useExtension) {
        // Send as query params for simplicity as per discussion
        const stateVal = state ? '1' : '0'
        const bodyStr = `pin=${pin}&state=${stateVal}`

        const response = await this.proxyRequest('/pin', 'POST', bodyStr, {
          'Content-Type': 'application/x-www-form-urlencoded'
        })
        return response.data
      }

      // Send as form data instead of JSON (NodeMCU expects server.hasArg())
      // Use URLSearchParams for clearer form data
      const params = new URLSearchParams()
      params.append('pin', pin)
      params.append('state', state ? '1' : '0') // Changed to 1/0 for safety

      const response = await this.axiosInstance.post('/pin', params, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        }
      })
      console.log(`Pin ${pin} set successfully:`, response.data)
      return response.data
    } catch (error) {
      // If CORS fails, try without CORS
      if (!this.useExtension && (error.message.includes('CORS') || error.message.includes('Network Error'))) {
        console.log('CORS issue detected, trying alternative method for GPIO...')
        try {
          // Use fetch with form data and no-cors mode as fallback
          const formData = new URLSearchParams()
          formData.append('pin', pin)
          formData.append('state', state ? '1' : '0')

          const response = await fetch(`${this.baseURL}/pin`, {
            method: 'POST',
            mode: 'no-cors',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: formData
          })

          if (response.type === 'opaque') {
            console.log(`Pin ${pin} set successfully (no-cors mode)`)
            return { success: true, status: 'success' }
          }
        } catch (noCorsError) {
          console.log('No-cors GPIO also failed:', noCorsError.message)
        }
      }
      throw new Error(`Failed to set pin ${pin} state: ${error.message}`)
    }
  }

  async togglePin(pin) {
    if (!this.baseURL) {
      throw new Error('Not connected to any device')
    }

    try {
      console.log(`Toggling pin ${pin}`)

      if (this.useExtension) {
        const bodyStr = `pin=${pin}`
        const response = await this.proxyRequest('/toggle', 'POST', bodyStr, {
          'Content-Type': 'application/x-www-form-urlencoded'
        })
        return response.data
      }

      const params = new URLSearchParams()
      params.append('pin', pin)

      const response = await this.axiosInstance.post('/toggle', params, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        }
      })
      console.log(`Pin ${pin} toggled successfully:`, response.data)
      return response.data
    } catch (error) {
      // If CORS fails, try without CORS
      if (!this.useExtension && (error.message.includes('CORS') || error.message.includes('Network Error'))) {
        console.log('CORS issue detected, trying alternative method for GPIO toggle...')
        try {
          // Use fetch with form data and no-cors mode as fallback
          const formData = new URLSearchParams()
          formData.append('pin', pin)

          const response = await fetch(`${this.baseURL}/toggle`, {
            method: 'POST',
            mode: 'no-cors',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: formData
          })

          if (response.type === 'opaque') {
            console.log(`Pin ${pin} toggled successfully (no-cors mode)`)
            return { success: true, status: 'success' }
          }
        } catch (noCorsError) {
          console.log('No-cors GPIO toggle also failed:', noCorsError.message)
        }
      }
      throw new Error(`Failed to toggle pin ${pin}: ${error.message}`)
    }
  }

  async getAllPinStates() {
    if (!this.baseURL) {
      throw new Error('Not connected to any device')
    }

    try {
      if (this.useExtension) {
        const response = await this.proxyRequest('/pins')
        return response.data
      }

      const response = await this.axiosInstance.get('/pins')
      return response.data
    } catch (error) {
      // If CORS fails, return empty pin states
      if (!this.useExtension && (error.message.includes('CORS') || error.message.includes('Network Error'))) {
        console.log('CORS issue detected, returning empty pin states...')
        return {
          pinStates: {
            D0: "LOW", D1: "LOW", D2: "LOW", D3: "LOW", D4: "LOW",
            D5: "LOW", D6: "LOW", D7: "LOW", D8: "LOW", A0: "LOW"
          }
        }
      }
      throw new Error(`Failed to get pin states: ${error.message}`)
    }
  }

  async resetAllPins() {
    if (!this.baseURL) {
      throw new Error('Not connected to any device')
    }

    try {
      console.log('Resetting all pins')

      if (this.useExtension) {
        const response = await this.proxyRequest('/reset', 'POST')
        return response.data
      }

      const response = await this.axiosInstance.post('/reset', null, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        }
      })
      console.log('All pins reset successfully:', response.data)
      return response.data
    } catch (error) {
      // If CORS fails, try without CORS
      if (!this.useExtension && (error.message.includes('CORS') || error.message.includes('Network Error'))) {
        console.log('CORS issue detected, trying alternative method for reset...')
        try {
          // Use fetch with no-cors mode as fallback
          const response = await fetch(`${this.baseURL}/reset`, {
            method: 'POST',
            mode: 'no-cors',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            }
          })

          if (response.type === 'opaque') {
            console.log('All pins reset successfully (no-cors mode)')
            return { success: true, status: 'success' }
          }
        } catch (noCorsError) {
          console.log('No-cors reset also failed:', noCorsError.message)
        }
      }
      throw new Error(`Failed to reset pins: ${error.message}`)
    }
  }

  // Car movement commands - Corrected to Use NodeMCU endpoints
  async moveForward(speed = null) {
    return this.sendCarCommand('car/forward', speed)
  }

  async moveBackward(speed = null) {
    return this.sendCarCommand('car/backward', speed)
  }

  async turnLeft(speed = null) {
    return this.sendCarCommand('car/left', speed)
  }

  async turnRight(speed = null) {
    return this.sendCarCommand('car/right', speed)
  }

  async moveLeftForward(speed = null) {
    return this.sendCarCommand('car/lf', speed)
  }

  async moveRightForward(speed = null) {
    return this.sendCarCommand('car/rf', speed)
  }

  async moveLeftBackward(speed = null) {
    return this.sendCarCommand('car/lb', speed)
  }

  async moveRightBackward(speed = null) {
    return this.sendCarCommand('car/rb', speed)
  }

  async stop() {
    return this.sendCarCommand('car/stop')
  }

  async setSpeed(speed) {
    return this.sendCarCommand('car/speed', speed)
  }

  // Get connection status
  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      baseURL: this.baseURL,
      usingExtension: this.useExtension
    }
  }

  // Get device information
  async getDeviceInfo() {
    try {
      const status = await this.getStatus()
      return {
        name: status.name || 'Unknown Device',
        version: status.version || 'Unknown',
        device: status.device || 'nodemcu',
        mode: status.mode || 'wifi',
        ip: status.ip || (this.baseURL ? this.baseURL.replace('http://', '') : ''),
        ssid: status.ssid || 'Unknown',
        rssi: status.rssi || 0,
        mac: status.mac || 'Unknown',
        status: status.status || 'connected',
        direction: status.direction || 'STOP',
        speed: status.speed || 0,
        isMoving: status.isMoving || false,
        pinStates: status.pinStates || {}
      }
    } catch (error) {
      throw new Error(`Failed to get device info: ${error.message}`)
    }
  }
}

export const carService = new CarService()